import { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { useAuth } from '@/context';
import { useTheme } from '@/hooks';
import { HelpCenter } from '@/components/help';
import styles from './PlatformLayout.module.css';

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
}

interface NavCategory {
  id: string;
  label: string;
  icon: React.ReactNode;
  items: NavItem[];
}

// Category Icons
const CategoryIcons = {
  overview: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <rect x="2" y="2" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.5"/>
      <rect x="9" y="2" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.5"/>
      <rect x="2" y="9" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.5"/>
      <rect x="9" y="9" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.5"/>
    </svg>
  ),
  crossMda: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M3 7h10M10 4l3 3-3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M13 9H3M6 12L3 9l3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  institutions: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M14 14V4.5C14 3.67 13.33 3 12.5 3h-9C2.67 3 2 3.67 2 4.5V14M14 14H2M14 14H15M2 14H1M5.5 6H6M5.5 9H6M10 6H10.5M10 9H10.5M8 14V11.5H5.5V14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  config: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M8 2v2M8 12v2M2 8h2M12 8h2M3.76 3.76l1.41 1.41M10.83 10.83l1.41 1.41M3.76 12.24l1.41-1.41M10.83 5.17l1.41-1.41" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  ),
  monitoring: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <rect x="2" y="2" width="12" height="10" rx="1" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M5 14h6M8 12v2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      <path d="M5 7l2 2 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
};

// Navigation Item Icons
const NavIcons = {
  dashboard: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M2.25 9L9 2.25L15.75 9M4.5 7.5V14.25C4.5 14.6642 4.83579 15 5.25 15H7.5V11.25C7.5 10.8358 7.83579 10.5 8.25 10.5H9.75C10.1642 10.5 10.5 10.8358 10.5 11.25V15H12.75C13.1642 15 13.5 14.6642 13.5 14.25V7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  executive: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M2.25 13.5V9M6 13.5V5.25M9.75 13.5V7.5M13.5 13.5V3M15.75 13.5H2.25" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  analytics: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M14.25 14.25V6.75M9 14.25V3.75M3.75 14.25V10.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  transfers: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M3.75 7.5h10.5M11.25 4.5l3 3-3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M14.25 10.5H3.75M6.75 13.5l-3-3 3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  retirement: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <circle cx="9" cy="5.25" r="2.75" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M3.75 15.75v-1.5a5.25 5.25 0 0110.5 0v1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      <path d="M12.75 12.75l2.25 2.25M12.75 15l2.25-2.25" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  ),
  praad: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M2.25 3h13.5v2.25H2.25V3z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
      <path d="M3.75 5.25v9c0 .414.336.75.75.75h9c.414 0 .75-.336.75-.75v-9" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M7.5 9h3M7.5 11.25h3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  ),
  mdas: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M15.75 15.75V5.25C15.75 4.42 15.08 3.75 14.25 3.75H3.75C2.92 3.75 2.25 4.42 2.25 5.25v10.5M15.75 15.75H2.25M15.75 15.75H17.25M2.25 15.75H0.75M6 6.75h.75M6 10.5h.75M11.25 6.75H12M11.25 10.5H12M9 15.75V12.75H6V15.75" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  users: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M12 14.25V13.5C12 12.09 10.91 11 9.5 11H4.5C3.09 11 2 12.09 2 13.5v.75M7 8.25C8.24 8.25 9.25 7.24 9.25 6S8.24 3.75 7 3.75 4.75 4.76 4.75 6 5.76 8.25 7 8.25zM16 14.25V13.5c0-1.05-.64-1.95-1.55-2.33M11.7 3.92c.91.38 1.55 1.28 1.55 2.33 0 1.05-.64 1.95-1.55 2.33" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  staffService: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <circle cx="9" cy="5.25" r="2.75" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M3 15v-.75A5.25 5.25 0 016.75 9H7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      <rect x="9" y="10.5" width="6" height="5.25" rx="0.75" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M10.5 13.125h3M10.5 14.625h2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  ),
  recordTypes: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M3.75 3.75A1.5 1.5 0 015.25 2.25h5.25l4.5 4.5v9a1.5 1.5 0 01-1.5 1.5h-8.25a1.5 1.5 0 01-1.5-1.5V3.75z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M10.5 2.25v4.5h4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  classifications: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M9 2.25L3 5.25v3.84c0 3.64 2.56 7.04 6 7.91 3.44-.87 6-4.27 6-7.91V5.25L9 2.25z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  workflows: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <rect x="2.25" y="2.25" width="4.5" height="4.5" rx="0.75" stroke="currentColor" strokeWidth="1.5"/>
      <rect x="11.25" y="2.25" width="4.5" height="4.5" rx="0.75" stroke="currentColor" strokeWidth="1.5"/>
      <rect x="11.25" y="11.25" width="4.5" height="4.5" rx="0.75" stroke="currentColor" strokeWidth="1.5"/>
      <rect x="2.25" y="11.25" width="4.5" height="4.5" rx="0.75" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M6.75 4.5h4.5M13.5 6.75v4.5M6.75 13.5h4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  ),
  auditLogs: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <rect x="3.75" y="2.25" width="10.5" height="13.5" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M6.75 6h4.5M6.75 9h4.5M6.75 12h3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  ),
  announcements: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M13.5 6.75a1.5 1.5 0 011.5 1.5v1.5a1.5 1.5 0 01-1.5 1.5M3 6.75h7.5a1.5 1.5 0 011.5 1.5v1.5a1.5 1.5 0 01-1.5 1.5H3v-4.5zM3 11.25v3l3-3H3z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  support: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M15.75 10.125a6.75 6.75 0 01-13.5 0V6.375A6.375 6.375 0 018.625 0h.75A6.375 6.375 0 0115.75 6.375v3.75z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M6 7.5h.01M12 7.5h.01M7.5 11.25a2.25 2.25 0 003 0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M2.25 10.125V12a2.25 2.25 0 002.25 2.25M15.75 10.125V12a2.25 2.25 0 01-2.25 2.25" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
};

const navCategories: NavCategory[] = [
  {
    id: 'overview',
    label: 'Overview',
    icon: CategoryIcons.overview,
    items: [
      { label: 'Dashboard', path: '/platform/dashboard', icon: NavIcons.dashboard },
      { label: 'Executive View', path: '/platform/executive', icon: NavIcons.executive },
      { label: 'Analytics', path: '/platform/analytics', icon: NavIcons.analytics },
    ],
  },
  {
    id: 'crossMda',
    label: 'Cross-MDA Operations',
    icon: CategoryIcons.crossMda,
    items: [
      { label: 'Staff Transfers', path: '/platform/staff-transfers', icon: NavIcons.transfers },
      { label: 'Retirement Processing', path: '/platform/retirement', icon: NavIcons.retirement },
      { label: 'PRAAD Transfers', path: '/platform/praad-transfers', icon: NavIcons.praad },
    ],
  },
  {
    id: 'institutions',
    label: 'Institution Management',
    icon: CategoryIcons.institutions,
    items: [
      { label: 'MDAs', path: '/platform/mdas', icon: NavIcons.mdas },
      { label: 'Users', path: '/platform/users', icon: NavIcons.users },
      { label: 'Staff Service', path: '/platform/staff-service', icon: NavIcons.staffService },
    ],
  },
  {
    id: 'config',
    label: 'Configuration',
    icon: CategoryIcons.config,
    items: [
      { label: 'Record Types', path: '/platform/config/record-types', icon: NavIcons.recordTypes },
      { label: 'Classifications', path: '/platform/config/classifications', icon: NavIcons.classifications },
      { label: 'Workflow Templates', path: '/platform/config/workflows', icon: NavIcons.workflows },
    ],
  },
  {
    id: 'monitoring',
    label: 'Monitoring',
    icon: CategoryIcons.monitoring,
    items: [
      { label: 'Support Center', path: '/platform/support', icon: NavIcons.support },
      { label: 'Audit Logs', path: '/platform/audit-logs', icon: NavIcons.auditLogs },
      { label: 'Announcements', path: '/platform/announcements', icon: NavIcons.announcements },
    ],
  },
];

export function PlatformLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(['overview', 'crossMda']) // Default expanded
  );

  const handleLogout = async () => {
    await logout();
    navigate('/platform/login');
  };

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  return (
    <div className={styles.layout}>
      <aside className={clsx(styles.sidebar, isSidebarCollapsed && styles.collapsed)}>
        <div className={styles.sidebarHeader}>
          <div className={styles.logo}>
            <div className={styles.logoIcon}>
              <svg width="32" height="32" viewBox="0 0 48 48" fill="none">
                {/* Folder base */}
                <path
                  d="M4 14C4 11.7909 5.79086 10 8 10H18L22 14H40C42.2091 14 44 15.7909 44 18V38C44 40.2091 42.2091 42 40 42H8C5.79086 42 4 40.2091 4 38V14Z"
                  className={styles.logoFolderBase}
                />
                {/* Document pages */}
                <rect x="12" y="20" width="24" height="4" rx="1" className={styles.logoDocLine} />
                <rect x="12" y="26" width="18" height="3" rx="1" className={styles.logoDocLine} />
                <rect x="12" y="31" width="20" height="3" rx="1" className={styles.logoDocLine} />
                {/* Ghana star accent */}
                <circle cx="38" cy="8" r="6" className={styles.logoStarCircle} />
                <text x="38" y="11" textAnchor="middle" className={styles.logoStarText}>★</text>
              </svg>
            </div>
            {!isSidebarCollapsed && (
              <div className={styles.logoText}>
                <span className={styles.logoTitle}>OHCS</span>
                <span className={styles.logoSubtitle}>Platform Admin</span>
              </div>
            )}
          </div>
          <button
            className={styles.collapseButton}
            onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            aria-label={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d={isSidebarCollapsed ? "M6 4L10 8L6 12" : "M10 4L6 8L10 12"} stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        <nav className={styles.nav}>
          {navCategories.map((category, categoryIndex) => {
            const isExpanded = expandedCategories.has(category.id);

            return (
              <div
                key={category.id}
                className={styles.category}
                style={{ '--category-index': categoryIndex } as React.CSSProperties}
              >
                {!isSidebarCollapsed ? (
                  <>
                    <button
                      className={clsx(styles.categoryHeader, isExpanded && styles.expanded)}
                      onClick={() => toggleCategory(category.id)}
                      aria-expanded={isExpanded}
                    >
                      <span className={styles.categoryIcon}>{category.icon}</span>
                      <span className={styles.categoryLabel}>{category.label}</span>
                      <span className={styles.categoryChevron}>
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                          <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </span>
                    </button>

                    <div className={clsx(styles.categoryItems, isExpanded && styles.expanded)}>
                      <ul className={styles.navList}>
                        {category.items.map((item, itemIndex) => (
                          <li
                            key={item.path}
                            style={{ '--item-index': itemIndex } as React.CSSProperties}
                          >
                            <NavLink
                              to={item.path}
                              className={({ isActive }) =>
                                clsx(styles.navItem, isActive && styles.active)
                              }
                            >
                              <span className={styles.navIcon}>{item.icon}</span>
                              <span className={styles.navLabel}>{item.label}</span>
                            </NavLink>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </>
                ) : (
                  // Collapsed mode - show items directly
                  category.items.map((item) => (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      className={({ isActive }) =>
                        clsx(styles.navItemCollapsed, isActive && styles.active)
                      }
                      title={item.label}
                    >
                      <span className={styles.navIcon}>{item.icon}</span>
                    </NavLink>
                  ))
                )}
              </div>
            );
          })}
        </nav>

        <div className={styles.sidebarFooter}>
          {/* Ghana flag colors accent */}
          <div className={styles.footerAccent} />
          <button className={styles.logoutButton} onClick={handleLogout}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M6.75 15H4.5A1.5 1.5 0 013 13.5V4.5A1.5 1.5 0 014.5 3h2.25M12 12.75L15.75 9 12 5.25M15.75 9H6.75" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            {!isSidebarCollapsed && <span>Logout</span>}
          </button>
        </div>
      </aside>

      <div className={styles.main}>
        <header className={styles.header}>
          <div className={styles.headerLeft}>
            <h1 className={styles.headerTitle}>Platform Administration</h1>
          </div>
          <div className={styles.headerRight}>
            {/* Help Button */}
            <button onClick={() => setIsHelpOpen(true)} className={styles.headerButton} aria-label="Help Center">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                <circle cx="12" cy="17" r="0.5" fill="currentColor" />
              </svg>
            </button>

            {/* Theme Toggle */}
            <button onClick={toggleTheme} className={styles.headerButton} aria-label="Toggle theme">
              {theme === 'dark' ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="5" />
                  <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
                </svg>
              )}
            </button>

            <div className={styles.userMenu}>
              <div className={styles.userAvatar}>
                {user?.firstName?.charAt(0) || 'A'}
              </div>
              <div className={styles.userInfo}>
                <span className={styles.userName}>{user?.firstName} {user?.lastName}</span>
                <span className={styles.userRole}>Platform Admin</span>
              </div>
            </div>
            <button className={styles.headerLogout} onClick={handleLogout} title="Logout">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M6.75 15H4.5A1.5 1.5 0 013 13.5V4.5A1.5 1.5 0 014.5 3h2.25M12 12.75L15.75 9 12 5.25M15.75 9H6.75" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        </header>

        <main className={styles.content}>
          <Outlet />
        </main>
      </div>

      {/* Help Center */}
      <HelpCenter
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        isPlatformUser={true}
      />
    </div>
  );
}
